import { useState, useRef, useCallback } from "react";

interface DonutChartSlice {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  slices: DonutChartSlice[];
  /** Text shown in the center of the donut. */
  centerLabel?: string | undefined;
  /** Large value shown in the center of the donut. */
  centerValue?: string | undefined;
  title?: string | undefined;
  /** Format slice values in the legend and tooltip. Defaults to locale string. */
  formatValue?: ((v: number) => string) | undefined;
}

interface HoverState {
  index: number;
  x: number;
  y: number;
}

const SIZE = 160;
const STROKE = 28;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CENTER = SIZE / 2;

export function DonutChart({
  slices,
  centerLabel,
  centerValue,
  title,
  formatValue,
}: DonutChartProps) {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const fmt = formatValue ?? ((v: number) => v.toLocaleString());

  const [hover, setHover] = useState<HoverState | undefined>(undefined);
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const containerRef = useRef<SVGSVGElement>(null);

  const handleSliceEnter = useCallback((index: number, e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({
      index,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  const handleSliceMove = useCallback(
    (e: React.MouseEvent) => {
      if (hover === undefined) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setHover((prev) =>
        prev
          ? { ...prev, x: e.clientX - rect.left, y: e.clientY - rect.top }
          : undefined,
      );
    },
    [hover],
  );

  const handleSliceLeave = useCallback(() => {
    setHover(undefined);
  }, []);

  // Build arc segments
  let offset = 0;
  const segments = slices.map((slice, i) => {
    const pct = total > 0 ? slice.value / total : 0;
    const dashLength = pct * CIRCUMFERENCE;
    const gapLength = CIRCUMFERENCE - dashLength;
    const segment = {
      ...slice,
      index: i,
      dashArray: `${dashLength} ${gapLength}`,
      dashOffset: -offset,
      pct,
    };
    offset += dashLength;
    return segment;
  });

  return (
    <div className="flex flex-col items-center">
      {title && (
        <div className="mb-2 text-center text-xs font-bold text-foreground">
          {title}
        </div>
      )}
      <div className="relative">
        <svg
          ref={containerRef}
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          onMouseMove={handleSliceMove}
          onMouseLeave={handleSliceLeave}
        >
          {/* Background ring */}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            className="text-content-faint"
            strokeWidth={STROKE}
          />
          {/* Segments */}
          {segments.map((seg) => (
            <circle
              key={seg.label}
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke={seg.color}
              strokeWidth={STROKE}
              strokeDasharray={seg.dashArray}
              strokeDashoffset={seg.dashOffset}
              strokeLinecap="butt"
              className="transition-opacity duration-150"
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "center",
                opacity:
                  hover !== undefined && hover.index !== seg.index ? 0.3 : 1,
              }}
              onMouseEnter={(e) => handleSliceEnter(seg.index, e)}
            />
          ))}
          {/* Center text */}
          {(centerValue || centerLabel) && (
            <>
              {centerValue && (
                <text
                  x={CENTER}
                  y={centerLabel ? CENTER - 4 : CENTER + 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-foreground text-lg font-bold"
                >
                  {centerValue}
                </text>
              )}
              {centerLabel && (
                <text
                  x={CENTER}
                  y={centerValue ? CENTER + 14 : CENTER + 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-foreground/40 text-[10px] font-medium"
                >
                  {centerLabel}
                </text>
              )}
            </>
          )}
        </svg>

        {/* Tooltip */}
        {hover !== undefined && segments[hover.index] && (
          <div
            className="pointer-events-none absolute z-50 -translate-x-1/2 -translate-y-full rounded-md border border-border bg-card px-2.5 py-1.5 shadow-lg"
            style={{ left: hover.x, top: hover.y - 8 }}
          >
            <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              <div
                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: segments[hover.index]!.color }}
              />
              <span className="text-content-secondary">
                {segments[hover.index]!.label}:
              </span>
              <span className="font-bold">
                {fmt(segments[hover.index]!.value)}
              </span>
              <span className="text-content-muted">
                ({(segments[hover.index]!.pct * 100).toFixed(1)}%)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1">
        {slices.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5">
            <div
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-[10px] font-medium text-content-secondary">
              {s.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
